import { useParams } from 'react-router-dom';
import { useProject } from '../hooks/useProject';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/layout/Navigation';
import Footer from '../components/layout/Footer';
import ProjectLogin from '../components/auth/ProjectLogin';
import ArchivedProject from '../components/project/ArchivedProject';
import SectionHero from '../components/sections/SectionHero';
import SectionProjet from '../components/sections/SectionProjet';
import SectionAbout from '../components/sections/SectionAbout';
import SectionBrief from '../components/sections/SectionBrief';
import SectionEtapes from '../components/sections/SectionEtapes';
import SectionFormules from '../components/sections/SectionFormules';
import SectionInclusions from '../components/sections/SectionInclusions';
import SectionOptions from '../components/sections/SectionOptions';
import SectionMarche from '../components/sections/SectionMarche';
import SectionPlanning from '../components/sections/SectionPlanning';
import SectionContact from '../components/sections/SectionContact';

export default function PropositionPage() {
  const { slug } = useParams();
  const { project, loading, error } = useProject(slug);
  const { isProjectAuthenticated, isAdminAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-secondary-600">Chargement du projet...</p>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <h1 className="text-6xl font-bold text-secondary-300 mb-4">404</h1>
          <h2 className="text-2xl font-bold mb-4">Projet introuvable</h2>
          <p className="text-secondary-600 mb-8">
            {error || `Le projet "${slug}" n'existe pas.`}
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition"
          >
            ← Retour à l'accueil
          </a>
        </div>
      </div>
    );
  }

  // Check if project is archived
  if (project.archived) {
    // If archived and user is not admin, show archived message
    if (!isAdminAuthenticated) {
      return <ArchivedProject project={project} />;
    }
    // If admin, continue to show the project (no authentication needed)
  } else {
    // Check authentication only for non-archived projects
    if (!isProjectAuthenticated(slug)) {
      return (
        <ProjectLogin
          projectSlug={slug}
          projectTitle={project.title}
          password={project.password}
        />
      );
    }
  }

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <Navigation
        sections={project.sections}
        project={project}
      />

      {/* Sections */}
      <main className="pt-20">
        <SectionHero
          data={project.content.hero}
          project={project}
          heroImage={project.branding?.heroImage}
        />
        <SectionProjet data={project.content.projet} />
        <SectionAbout data={project.content.about} />
        <SectionBrief brief={project.content.brief} />
        <SectionEtapes data={project.content.etapes} />
        <SectionFormules
          formules={project.formules}
          comparison={project.comparison}
          savings={project.savings}
        />
        <SectionInclusions data={project.content.inclusions} />
        <SectionOptions data={project.content.options} />
        {project.sections.find(s => s.id === 'marche')?.enabled && (
          <SectionMarche data={project.content.marche} />
        )}
        <SectionPlanning planning={project.planning} />
        <SectionContact data={project.content.contact} />
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}
